import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface MailchimpResponse {
  result: {
    result: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MailchimpService {
  constructor(private functions: Functions) {}

  /**
   * Check if a user is subscribed to the Mailchimp audience
   * @param email User's email address
   * @returns Observable<string> Subscription status ('subscribed', 'unsubscribed', 'cleaned', 'pending')
   */
  checkSubscriptionStatus(email: string): Observable<MailchimpResponse> {
    const checkSubscription = httpsCallable<{ email: string }, MailchimpResponse>(
      this.functions,
      'checkMailchimpSubscription'
    );
    return from(checkSubscription({ email })).pipe(
      map(response => response.data)
    );
  }

  /**
   * Add a user to Mailchimp audience
   * @param email User's email address
   * @param mergeFields Optional merge fields (e.g., FNAME, LNAME)
   */
  addToList(email: string, mergeFields?: Record<string, any>): Observable<any> {
    const addToList = httpsCallable<{ email: string, mergeFields?: Record<string, any> }, any>(
      this.functions,
      'addUserToList'
    );
    return from(addToList({ email, mergeFields })).pipe(
      map(response => response.data)
    );
  }

  /**
   * Remove a user from Mailchimp audience
   * @param email User's email address
   */
  removeFromList(email: string): Observable<any> {
    const removeFromList = httpsCallable<{ email: string }, any>(
      this.functions,
      'removeUserFromList'
    );
    return from(removeFromList({ email })).pipe(
      map(response => response.data)
    );
  }

  /**
   * Update user's Mailchimp merge fields
   * @param email User's email address
   * @param mergeFields Merge fields to update
   */
  updateMergeFields(email: string, mergeFields: Record<string, any>): Observable<any> {
    const updateFields = httpsCallable<{ email: string, mergeFields: Record<string, any> }, any>(
      this.functions,
      'mergeFieldsHandler'
    );
    return from(updateFields({ email, mergeFields })).pipe(
      map(response => response.data)
    );
  }

  /**
   * Add tags to a user in Mailchimp
   * @param email User's email address
   * @param tags Tags to add
   */
  addTags(email: string, tags: string[]): Observable<any> {
    const addTags = httpsCallable<{ email: string, tags: string[] }, any>(
      this.functions,
      'memberTagsHandler'
    );
    return from(addTags({ email, tags })).pipe(
      map(response => response.data)
    );
  }
} 